import { Group, Stack, createStyles } from '@mantine/core';
import { format } from 'date-fns';
import { Fragment, useMemo, useState } from 'react';
import { IconLock, IconMenu2 } from '@tabler/icons-react';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';

import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';
import { Text } from '~/domains/design-system/Text';

import type { SegmentsFieldItemType, SegmentsFieldType, PayoutSegmentsValue } from '../types';
import { pluralizeSlateLabel } from '../../../../utils/pluralizeSlateLabel';
import useTranslation from 'next-translate/useTranslation';

const useEditStyles = createStyles((theme) => ({
  description: {
    display: 'block',
    margin: `${theme.spacing.xs}px 0`,
  },
  item: {
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.md / 2}px ${theme.spacing.md}px`,
  },
  segmentWrapper: {
    '&.isDragging': {
      opacity: 0.2,
    },
  },
  segment: {
    background: theme.colors.gray[7],
    color: theme.white,
    height: 44,
    position: 'relative',
    userSelect: 'none',

    '&.isDraggable': {
      cursor: 'grab',
    },
    '&:not(.isDraggable)': {
      cursor: 'not-allowed',
    },
  },
  dragIcon: {
    position: 'absolute',
    left: theme.spacing.xs,
  },
  slate: {
    border: `1px solid ${theme.colors.gray[3]}`,
    transition: 'margin-top 100ms ease',
    userSelect: 'none',

    '&.isOver': {
      marginTop: 44 + theme.spacing.xs,
    },
  },
  slateLabel: {
    flexShrink: 1,
  },
  slateDate: {
    flexShrink: 0,
  },
}));

function SegmentEditSlate({
  slate,
  isDropDisabled,
}: {
  slate: SegmentsFieldItemType;
  isDropDisabled: boolean;
}) {
  const { classes, cx } = useEditStyles();
  const { isOver, setNodeRef } = useDroppable({
    id: slate.key,
    disabled: isDropDisabled,
  });

  return (
    <div ref={setNodeRef}>
      <Group
        key={slate.key}
        position="apart"
        className={cx(classes.item, classes.slate, { isOver })}
        noWrap
        data-test-id={dt.segments.edit.slate}
      >
        <Text variant="subhead-medium" className={classes.slateLabel}>
          {slate.label}
        </Text>
        <Text variant="body-large" className={classes.slateDate}>
          {format(slate.date, 'M/dd')}
        </Text>
      </Group>
    </div>
  );
}

function SegmentEditSegmentDumb({ index, isDraggable }: { index: number; isDraggable: boolean }) {
  const { classes, cx } = useEditStyles();
  const { t } = useTranslation('contest');

  return (
    <Group
      className={cx(classes.item, classes.segment, { isDraggable })}
      position="center"
      spacing="xs"
      data-test-id={dt.segments.edit.segment}
    >
      <Text variant="headline-medium">
        {t('manage.common.fields.segmentsEdit.segment', { index: index + 1 })}
      </Text>
      {!isDraggable ? <IconLock stroke={1.5} /> : <IconMenu2 className={classes.dragIcon} />}
    </Group>
  );
}

function SegmentEditSegment({ index, isDraggable }: { index: number; isDraggable: boolean }) {
  const { classes, cx } = useEditStyles();
  const {
    //
    attributes,
    listeners,
    setNodeRef,
    isDragging,
  } = useDraggable({
    id: index,
    disabled: !isDraggable,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cx(classes.segmentWrapper, { isDragging })}
    >
      <SegmentEditSegmentDumb index={index} isDraggable={isDraggable} />
    </div>
  );
}

export function SegmentsEdit({
  slates,
  rules,
  value,
  onChange,
  slateLabel = 'tournaments',
  canEditSlates = true,
}: {
  slates: SegmentsFieldItemType[];
  rules: SegmentsFieldType['rules'];
  value: PayoutSegmentsValue;
  slateLabel?: string;
  onChange: (
    newValueOrCb: PayoutSegmentsValue | ((prev: PayoutSegmentsValue) => PayoutSegmentsValue)
  ) => void;
  canEditSlates?: boolean;
}) {
  const { classes } = useEditStyles();
  const { t } = useTranslation('contest');
  const [activeIndex, setActiveIndex] = useState<number>(null);

  const mouseSensor = useSensor(MouseSensor);
  const touchSensor = useSensor(TouchSensor);
  const sensors = useSensors(mouseSensor, touchSensor);

  const slatesMap = useMemo(
    () =>
      slates.reduce((acc, slate) => {
        acc[slate.key] = slate;
        return acc;
      }, {} as Record<string, SegmentsFieldItemType>),
    [slates]
  );

  const sortedEntries = useMemo(
    () => Object.entries(value).sort(([, a], [, b]) => a.sortOrder - b.sortOrder),
    [value]
  );

  const activeEntry = sortedEntries[activeIndex];
  const prevEntry = sortedEntries[activeIndex - 1];
  const activeItemSlateIds = activeEntry?.[1]?.slateGroupIds;

  const handleDragStart = (event) => {
    if (!canEditSlates) return;
    setActiveIndex(event.active.id);
  };
  const handleDragEnd = (event) => {
    if (!canEditSlates) return;

    const overId = event.over?.id;
    if (overId && prevEntry && activeEntry) {
      const [prevId, prevSegment] = prevEntry;
      const [activeId] = activeEntry;
      const possibleSlateIds = [...prevSegment.slateGroupIds, ...activeItemSlateIds];
      const overSlateIndex = possibleSlateIds.indexOf(overId);
      if (!overSlateIndex) {
        setActiveIndex(null);
        return;
      }

      onChange((segments) => ({
        ...segments,
        [prevId]: { ...segments[prevId], slateGroupIds: possibleSlateIds.slice(0, overSlateIndex) },
        [activeId]: {
          ...segments[activeId],
          slateGroupIds: possibleSlateIds.slice(overSlateIndex),
        },
      }));
    }
    setActiveIndex(null);
  };

  const droppableSlatesIds = useMemo(() => {
    if (!activeItemSlateIds || !prevEntry) {
      return [];
    }

    return [
      ...(prevEntry[1].slateGroupIds.slice(rules.minSlateGroupsPerSegment) ?? []),
      ...(activeItemSlateIds.slice(1, -rules.minSlateGroupsPerSegment + 1) ?? []),
    ];
  }, [activeItemSlateIds, prevEntry, rules.minSlateGroupsPerSegment]);

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
      sensors={sensors}
    >
      <div>
        <Text variant="body-medium" className={classes.description}>
          {t('manage.common.fields.segmentsEdit.description', {
            minCount: rules.minSlateGroupsPerSegment,
            slateLabel: slateLabel.toLowerCase(),
            slateLabelPlural: pluralizeSlateLabel(
              rules.minSlateGroupsPerSegment,
              slateLabel
            ).toLowerCase(),
          })}
        </Text>
        <Stack spacing="xs">
          {sortedEntries.map(([id, segment], index) => {
            const prevSegmentSlateIds = sortedEntries[index - 1]?.[1]?.slateGroupIds;
            const hasPrevSegment = Boolean(prevSegmentSlateIds);
            const currentSegmentHasExtraSlates =
              segment.slateGroupIds.length > rules.minSlateGroupsPerSegment;
            const prevSegmentHasExtraSlates =
              prevSegmentSlateIds && prevSegmentSlateIds.length > rules.minSlateGroupsPerSegment;
            const canMoveSlatesBetweenSegments =
              hasPrevSegment && (currentSegmentHasExtraSlates || prevSegmentHasExtraSlates);
            const isDraggable = canEditSlates && canMoveSlatesBetweenSegments;
            return (
              <Fragment key={id}>
                <SegmentEditSegment index={index} isDraggable={isDraggable} />
                {segment.slateGroupIds.map((slateId) => {
                  const slate = slatesMap[slateId];
                  if (!slate) {
                    return null;
                  }

                  return (
                    <SegmentEditSlate
                      key={slateId}
                      slate={slate}
                      isDropDisabled={!droppableSlatesIds.includes(slateId)}
                    />
                  );
                })}
              </Fragment>
            );
          })}
        </Stack>
      </div>
      <DragOverlay modifiers={[restrictToWindowEdges]}>
        {activeIndex ? <SegmentEditSegmentDumb index={activeIndex} isDraggable /> : null}
      </DragOverlay>
    </DndContext>
  );
}
